import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, DollarSign, Mountain, Calendar, MapPin, Check, ChevronRight, Heart, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";
import { getImageUrl } from "@/utils/imageUrl";

const difficultyColors = {
  "Fácil": "bg-green-100 text-green-800",
  "Moderado": "bg-yellow-100 text-yellow-800",
  "Difícil": "bg-red-100 text-red-800",
};

export default function GuiaDetailPage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState(false);

  useEffect(() => {
    const fetchGuide = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/guides/${slug}`);
        setGuide(response.data);
      } catch (error) {
        console.error("Error fetching guide:", error);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchGuide();
  }, [slug]);

  const handleVote = async () => {
    if (voted || !guide) return;
    // Similar to memory voting
    setVoted(true);
    toast.success("¡Gracias por tu voto!");
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-gray-200 rounded mb-8" />
            <div className="h-12 w-3/4 bg-gray-200 rounded mb-4" />
            <div className="aspect-video bg-gray-200 rounded-3xl mb-8" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!guide) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-600 mb-4">
            Guía no encontrada
          </h1>
          <Link to="/guias">
            <Button className="rounded-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Guías
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const placeholderImage = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=85";

  return (
    <Layout>
      {/* Hero Image */}
      <section className="relative h-[50vh] md:h-[60vh]">
        <img
          src={getImageUrl(guide.featured_image_url) || placeholderImage}
          alt={guide.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/guias"
              className="inline-flex items-center text-white/80 hover:text-white transition-colors mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Guías
            </Link>
            
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {guide.difficulty && (
                <Badge className={difficultyColors[guide.difficulty]}>
                  {guide.difficulty}
                </Badge>
              )}
              {guide.best_season && (
                <Badge className="bg-white/20 text-white border-0">
                  <Calendar className="h-3 w-3 mr-1" />
                  {guide.best_season}
                </Badge>
              )}
            </div>
            
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              {guide.title}
            </h1>
          </div>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="guide-detail">
        {/* Quick Info */}
        <div className="bg-white rounded-2xl p-6 shadow-card -mt-16 relative z-10 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {guide.duration && (
              <div className="text-center">
                <Clock className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-brand-textMuted">Duración</p>
                <p className="font-medium text-brand-text">{guide.duration}</p>
              </div>
            )}
            {guide.estimated_cost && (
              <div className="text-center">
                <DollarSign className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-brand-textMuted">Costo aprox.</p>
                <p className="font-medium text-brand-text">{guide.estimated_cost}</p>
              </div>
            )}
            {guide.difficulty && (
              <div className="text-center">
                <Mountain className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-brand-textMuted">Dificultad</p>
                <p className="font-medium text-brand-text">{guide.difficulty}</p>
              </div>
            )}
            {guide.municipio && (
              <div className="text-center">
                <MapPin className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-brand-textMuted">Ubicación</p>
                <p className="font-medium text-brand-text">{guide.municipio}</p>
              </div>
            )}
          </div>
        </div>

        {/* Excerpt */}
        {guide.excerpt && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xl text-brand-textSecondary leading-relaxed mb-8"
          >
            {guide.excerpt}
          </motion.p>
        )}

        {/* Checklist */}
        {guide.checklist && guide.checklist.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-green-50 rounded-2xl p-6 mb-8"
          >
            <h2 className="font-heading text-xl font-bold text-brand-text mb-4 flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Qué llevar
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {guide.checklist.map((item, idx) => (
                <label key={idx} className="flex items-center gap-2 text-brand-text cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" />
                  {item}
                </label>
              ))}
            </div>
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose-content max-w-none mb-8"
          dangerouslySetInnerHTML={{ __html: guide.content_html || "" }}
        />

        {/* Itinerary Steps */}
        {guide.steps && guide.steps.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="font-heading text-2xl font-bold text-brand-text mb-6">
              Itinerario
            </h2>
            <div className="space-y-6">
              {guide.steps.map((step, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1 bg-white rounded-2xl p-5 shadow-card">
                    <h3 className="font-heading font-bold text-brand-text mb-2">
                      {step.title}
                    </h3>
                    {step.time && (
                      <p className="text-sm text-brand-textMuted mb-2 flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {step.time}
                      </p>
                    )}
                    <p className="text-brand-textSecondary">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tips */}
        {guide.tips && guide.tips.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-yellow-50 rounded-2xl p-6 mb-8"
          >
            <h2 className="font-heading text-xl font-bold text-brand-text mb-4">
              💡 Tips del local
            </h2>
            <ul className="space-y-3">
              {guide.tips.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-brand-text">
                  <ChevronRight className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  {tip}
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Gallery */}
        {guide.gallery_urls && guide.gallery_urls.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-8"
          >
            <h2 className="font-heading text-2xl font-bold text-brand-text mb-4">
              Galería
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {guide.gallery_urls.map((img, idx) => (
                <img
                  key={idx}
                  src={img.url}
                  alt={`Foto ${idx + 1}`}
                  className="w-full aspect-square object-cover rounded-2xl"
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Vote & Share */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
          <Button
            onClick={handleVote}
            disabled={voted}
            variant={voted ? "secondary" : "default"}
            className="rounded-full"
          >
            <Heart className={`h-5 w-5 mr-2 ${voted ? "fill-current" : ""}`} />
            {voted ? "¡Te gustó!" : "Me gusta esta guía"}
          </Button>
          
          <div className="flex items-center gap-2">
            <span className="text-brand-textSecondary flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Compartir
            </span>
          </div>
        </div>
      </article>
    </Layout>
  );
}
